<template>
    <Loading v-if="loading"></Loading>

    <div v-if="notifications.length > 0" class="nc-notifications">
        <template v-for="group in groups" :key="group.key">
            <section v-if="group.items.length > 0" class="nc-notifications__group">
                <h6 class="nc-notifications__label">{{ group.label }}</h6>

                <ion-item-sliding v-for="notification in group.items" :key="notification.id">
                    <ion-item button lines="none" :detail="false" class="nc-notification"
                        :class="{ 'nc-notification--unread': !notification.read_at }"
                        @click="open(notification)">
                        <div class="nc-notification__icon" slot="start">
                            <mdicon :name="iconFor(notification)" :size="20" />
                        </div>
                        <div class="nc-notification__body">
                            <p class="nc-notification__message">{{ notification.data.message }}</p>
                            <span class="nc-notification__time">{{ $fuzzyDate(notification.created_at, true) }}</span>
                        </div>
                        <span v-if="!notification.read_at" class="nc-notification__dot" slot="end"></span>
                    </ion-item>

                    <ion-item-options side="end">
                        <ion-item-option color="danger" @click="remove(notification, $event)">
                            <mdicon name="trash-can-outline" />
                        </ion-item-option>
                    </ion-item-options>
                </ion-item-sliding>
            </section>
        </template>
    </div>

    <div v-else class="nc-notifications__empty">
        <div class="nc-notifications__empty-icon">
            <mdicon name="bell-outline" :size="36" />
        </div>
        <p class="nc-notifications__empty-title">Geen notificaties</p>
        <p class="nc-notifications__empty-text">Nieuwe meldingen verschijnen hier zodra ze binnenkomen.</p>
    </div>
</template>

<script>
import { db } from "@/services/db";
import {
    modalController,
    IonItem, IonItemOptions, IonItemOption, IonItemSliding,
} from "@ionic/vue";
import Loading from "./Loading.vue";
import PDFviewer from "./PDFviewer.vue";

export default {
    name: 'notifications-index',
    components: {
        IonItem,
        IonItemOptions,
        IonItemOption,
        IonItemSliding,
        Loading,
    },
    props: {
        notifications: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['navigate'],
    data() {
        return {
            loading: false,
        }
    },
    computed: {
        groups() {
            return [
                { key: 'unread', label: 'Nieuw', items: this.notifications.filter(n => !n.read_at) },
                { key: 'read', label: 'Eerder', items: this.notifications.filter(n => n.read_at) },
            ]
        },
    },
    methods: {
        iconFor(notification) {
            const data = notification.data || {}

            if (data.icon === 'chat') return 'chat-outline'
            if (data.actionText === 'Downloaden') return 'file-download-outline'
            if (data.subject_type === 'inspection') return 'clipboard-check-outline'
            if (data.subject_type === 'action') return 'calendar-check-outline'

            return 'information-outline'
        },
        markAsRead(notification) {
            if (notification.read_at) return

            // Lokaal-first: de patch schrijft direct naar Dexie zodat de lijst en
            // de badge meteen bijwerken; de server volgt via de job queue.
            this.$requests.patch(
                'notifications',
                '/notifications/' + notification.id,
                { ...notification, read_at: new Date().toISOString() }
            )
        },
        async open(notification) {
            this.markAsRead(notification)

            const data = notification.data || {}

            if (data.subject_type === 'action') {
                const action = await db.actions.where({ uuid: data.subject_uuid }).first()

                if (!action) {
                    this.$toast.error('Deze actie is niet meer beschikbaar')
                    return
                }

                this.$emit('navigate')
                this.$router.push({ name: 'action-edit', query: { uuid: action.uuid } })
            } else if (data.actionText === 'Downloaden') {
                const modal = await modalController.create({
                    component: PDFviewer,
                    componentProps: {
                        url: data.actionUrl?.url,
                    },
                    initialBreakpoint: 1,
                    breakpoints: [0, 1],
                })

                await modal.present()
            } else if (data.subject_type === 'inspection') {
                this.openInspection(data.subject_uuid)
            } else {
                this.$toast.error('Deze notificatie is niet beschikbaar')
            }
        },
        async openInspection(inspection_uuid) {
            this.loading = true

            try {
                const inspections = await this.$requests.get('inspections', '/inspections')
                const inspection = inspections.find(item => item.uuid === inspection_uuid)

                if (!inspection) {
                    this.$toast.error('Deze inspectie is niet meer beschikbaar')
                    return
                }

                this.$store.dispatch('setInspectionId', inspection.id)
                this.$store.dispatch('setLocationId', inspection.location_id)
                this.$store.dispatch('setInspectionUuid', inspection.uuid)
                this.$store.dispatch('setLocationUuid', inspection.location_uuid)

                this.$emit('navigate')

                this.$router.push({
                    name: 'inspection-show',
                    params: {
                        id: inspection.id,
                    },
                    query: {
                        location_id: inspection.location_id,
                        inspection_uuid: inspection.uuid,
                        location_uuid: inspection.location_uuid,
                    }
                })
            } finally {
                this.loading = false
            }
        },
        async remove(notification, $event) {
            const sliding = $event?.target?.closest('ion-item-sliding')
            if (sliding) await sliding.close()

            // Verwijdert lokaal uit Dexie en zet een delete-job in de queue;
            // de liveQuery in SideMenu haalt het item uit de lijst.
            await this.$requests.delete(
                'notifications',
                '/notifications/' + notification.id,
                notification.id
            )
        },
    },
}
</script>

<style scoped>
.nc-notifications {
    padding-bottom: var(--nc-safe-area-bottom);
}

.nc-notifications__label {
    margin: 0;
    padding: var(--nc-spacing-md) var(--nc-spacing-md) var(--nc-spacing-xs);
    font-size: 11px;
    font-weight: var(--nc-font-weight-bold);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--nc-text-muted);
}

.nc-notification {
    --background: var(--nc-bg);
    --padding-start: var(--nc-spacing-md);
    --padding-top: 10px;
    --padding-bottom: 10px;
    --inner-padding-end: var(--nc-spacing-md);
    --border-color: var(--nc-border);
    --color: var(--nc-text);
    font-family: var(--nc-font-family);
}

.nc-notification--unread {
    --background: var(--nc-color-primary-light);
}

.nc-notification__icon {
    width: 38px;
    height: 38px;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: var(--nc-radius-full);
    background: var(--nc-color-light);
    color: var(--nc-text-muted);
}

.nc-notification--unread .nc-notification__icon {
    background: var(--nc-color-primary);
    color: var(--nc-text-on-primary);
}

.nc-notification__body {
    min-width: 0;
    padding: 2px 0;
}

.nc-notification__message {
    margin: 0;
    font-size: var(--nc-font-size-xs);
    line-height: 1.4;
    color: var(--nc-text);
    white-space: normal;
}

.nc-notification--unread .nc-notification__message {
    font-weight: var(--nc-font-weight-medium);
}

.nc-notification__time {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: var(--nc-text-muted);
}

.nc-notification__dot {
    width: 8px;
    height: 8px;
    margin-left: 8px;
    flex-shrink: 0;
    border-radius: var(--nc-radius-full);
    background: var(--nc-color-primary);
}

.nc-notifications__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: var(--nc-spacing-xl);
    text-align: center;
}

.nc-notifications__empty-icon {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--nc-radius-full);
    background: var(--nc-color-light);
    color: var(--nc-text-muted);
    margin-bottom: var(--nc-spacing-md);
}

.nc-notifications__empty-title {
    margin: 0;
    font-size: var(--nc-font-size-sm);
    font-weight: var(--nc-font-weight-bold);
    color: var(--nc-text);
}

.nc-notifications__empty-text {
    margin: var(--nc-spacing-xs) 0 0;
    font-size: var(--nc-font-size-xs);
    color: var(--nc-text-muted);
}
</style>
